/**
 * Drives a Dawn <slider-component> on a timer.
 *
 * Rendered as an empty child of the slider it controls, so it upgrades together
 * with markup that is injected asynchronously (product recommendations, etc.).
 *
 *   <slider-component>
 *     <ul id="Slider-x" class="slider">...</ul>
 *     <div class="slider-buttons">...</div>
 *     <slider-autoplay data-speed="4"></slider-autoplay>
 *   </slider-component>
 */
class SliderAutoplay extends HTMLElement {
  connectedCallback() {
    this.sliderComponent = this.closest('slider-component');
    if (!this.sliderComponent) return;

    this.interval = (parseFloat(this.dataset.speed) || 4) * 1000;
    // Rewind is opt-out, so sliders that omit the attribute keep looping.
    this.rewind = this.dataset.rewind !== 'false';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.timer = null;
    this.visible = false;
    this.hovered = false;
    this.finished = false;

    this.pause = this.pause.bind(this);
    this.resume = this.resume.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);

    this.sliderComponent.addEventListener('mouseenter', this.pause);
    this.sliderComponent.addEventListener('mouseleave', this.resume);
    this.sliderComponent.addEventListener('focusin', this.pause);
    this.sliderComponent.addEventListener('focusout', this.resume);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.observer = new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
      this.visible ? this.start() : this.stop();
    });
    this.observer.observe(this.sliderComponent);
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
    this.sliderComponent?.removeEventListener('mouseenter', this.pause);
    this.sliderComponent?.removeEventListener('mouseleave', this.resume);
    this.sliderComponent?.removeEventListener('focusin', this.pause);
    this.sliderComponent?.removeEventListener('focusout', this.resume);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  onVisibilityChange() {
    document.hidden ? this.stop() : this.start();
  }

  pause() {
    this.hovered = true;
    this.stop();
  }

  resume() {
    this.hovered = false;
    this.start();
  }

  start() {
    if (this.timer || this.finished || this.hovered || !this.visible || document.hidden) return;
    if (this.reducedMotion.matches) return;
    this.timer = setInterval(() => this.advance(), this.interval);
  }

  stop() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  advance() {
    const track = this.sliderComponent.querySelector('[id^="Slider-"]');
    // Nothing to scroll at this breakpoint — the grid is static.
    if (!track || track.scrollWidth <= track.clientWidth + 1) return;

    const nextButton = this.sliderComponent.querySelector('button[name="next"]');

    if (nextButton && !nextButton.hasAttribute('disabled')) {
      nextButton.click();
    } else if (this.rewind) {
      // Reached the end — loop back to the first slide.
      track.scrollTo({ left: 0 });
    } else {
      // Rewind is off: stop for good rather than idling on the last slide.
      this.finished = true;
      this.stop();
    }
  }
}

customElements.define('slider-autoplay', SliderAutoplay);
